import {
  AbstractMesh,
  Camera,
  Collider,
  Epsilon,
  Matrix,
  type Nullable,
  Observable,
  Scene,
  TargetCamera,
  Tools,
  TransformNode,
  Vector2,
  Vector3,
  Viewport,
} from '@babylonjs/core';

import { AnimationProperty, playAnimation, stopPlayingAnimation } from '../animation';
import { RenderManager } from '../manager';
import { clamp } from '../util/Util';

import { CameraAnimationOption, CameraInputParam, createEasingFunction, defaultCameraAnimationOption } from './data';
import { convertRightToLeftHanded, convertRightToLeftHandedRotation, getFitRadius, toRadian } from './ZEDCameraHelper';
import { ZEDCameraInputsManager } from './ZEDCameraInputManager';

export class ZEDCamera extends TargetCamera {
  public override getClassName(): string {
    return 'ZEDCamera';
  }

  public alpha: number = 0;
  public onChangedAlpha = new Observable<number>();
  public beta: number = 0;
  private _radius: number = Epsilon;
  public get radius() {
    return this._radius;
  }
  public set radius(value: number) {
    this._radius = Math.max(value, Epsilon);
  }

  public inertialAlphaOffset = 0;
  public inertialBetaOffset = 0;
  public inertialRadiusOffset = 0;
  public inertialTargetOffset = Vector3.Zero();
  public inertialFov = 0;
  public lowerAlphaLimit: Nullable<number> = null;
  public upperAlphaLimit: Nullable<number> = null;
  public lowerBetaLimit: Nullable<number> = 0.01;
  public upperBetaLimit: Nullable<number> = Math.PI - 0.01;
  public lowerRadiusLimit: Nullable<number> = null;
  public upperRadiusLimit: Nullable<number> = null;
  public lowerFovLimit: Nullable<number> = 0.4;
  public upperFovLimit: Nullable<number> = 2.5;

  public lowerGroundToDistance: Nullable<number> = null;

  public inertialPanningX: number = 0;
  public inertialPanningY: number = 0;

  public panningDistanceLimit: Nullable<number> = null;
  public upperTargetHeightLimit: Nullable<number> = null;
  public targetHeightPivot: Nullable<number> = null;
  public panningOriginTarget: Vector3 = Vector3.Zero();

  public panningInertia = 0.9;
  public xAxisPanningLock = false;
  public yAxisPanningLock = false;
  public zAxisPanningLock = false;

  public targetScreenOffset = Vector2.Zero();
  public allowUpsideDown = true;
  public override _viewMatrix = new Matrix();

  public inputManager: ZEDCameraInputsManager;

  public override _reset: () => void = () => {};

  public panningAxis: Vector3 = new Vector3(1, 1, 0);
  protected _transformedDirection: Vector3 = new Vector3();
  public onMeshTargetChangedObservable = new Observable<Nullable<AbstractMesh>>();
  protected _newPosition = Vector3.Zero();
  private _computationVector: Vector3 = Vector3.Zero();

  // public _localDirection: Vector3;
  // public _transformedDirection: Vector3;

  //----------------------------------------
  public readonly renderManager;
  constructor(
    name: string,
    renderManager: RenderManager,
    target?: Vector3,
    alpha?: number,
    beta?: number,
    radius?: number,
    scene?: Scene,
    setActiveOnSceneIfNoneActive = true
  ) {
    super(name, Vector3.Zero(), scene, setActiveOnSceneIfNoneActive);

    this.renderManager = renderManager;

    this._target = Vector3.Zero();
    if (target) {
      this.setTarget(target);
    }

    this.alpha = alpha ?? 0;
    this.beta = beta ?? 0;
    this.radius = radius ?? 0;

    this.getViewMatrix();
    this.inputManager = new ZEDCameraInputsManager(this);
  }

  public override dispose(): void {
    this.getDefaultExtends = undefined;
    this.inputManager.dispose();
    this.inputManager.clear();
    super.dispose();
  }

  //----------------------------------------
  public getEnableControl() {
    return this.inputManager.enable;
  }
  public getInputs() {
    return this.inputManager.getInputs();
  }
  public enableControl(enable: boolean) {
    if (this.getEnableControl() === enable) return;

    if (enable) {
      this.attachControl();
    } else {
      this.detachControl();
    }
  }
  public override attachControl(noPreventDefault?: boolean): void {
    this.inputManager.attachControl(noPreventDefault);

    this._reset = () => {
      this.inertialAlphaOffset = 0;
      this.inertialBetaOffset = 0;
      this.inertialRadiusOffset = 0;
      this.inertialTargetOffset = Vector3.Zero();
      this.inertialPanningX = 0;
      this.inertialPanningY = 0;
      this.inertialFov = 0;
    };
  }
  public override detachControl(): void {
    this.inputManager.detachControl();

    this.cameraDirection = new Vector3(0, 0, 0);
    this.cameraRotation = new Vector2(0, 0);

    if (this._reset) {
      this._reset();
    }
  }
  public attachInputs(inputs: CameraInputParam, noPreventDefault?: boolean) {
    this.inputManager.attachInputs(inputs, noPreventDefault);
  }

  //----------------------------------------
  protected _collider?: Collider | undefined;
  public checkCollisions = false;
  public collisionRadius = new Vector3(0.5, 0.5, 0.5);
  protected _collisionTriggered: boolean = false;
  protected _collisionVelocity = Vector3.Zero();
  protected _previousPosition = Vector3.Zero();
  public onCollide?: ((collidedMesh: AbstractMesh) => void) | undefined;

  protected _onCollisionPositionChange = (
    _collisionId: number,
    newPosition: Vector3,
    collidedMesh: Nullable<AbstractMesh> = null
  ) => {
    if (!collidedMesh) {
      this._previousPosition.copyFrom(this._position);
    } else {
      // 충돌로 인해 카메라 위치가 보정되면 target도 그 위치에 따라 보정
      const dir = this._target.subtract(this._position).normalize();
      this._target = newPosition.add(dir.scale(this.radius));
      this._setPosition(newPosition);
      this.inertia = 0;

      if (this.onCollide) {
        this.onCollide(collidedMesh);
      }
    }

    // Recompute because of constraints
    const cosa = Math.cos(this.alpha);
    const sina = Math.sin(this.alpha);
    const cosb = Math.cos(this.beta);
    let sinb = Math.sin(this.beta);

    if (sinb === 0) {
      sinb = 0.0001;
    }

    const target = this._getTargetPosition();
    this._computationVector.copyFromFloats(this.radius * cosa * sinb, this.radius * cosb, this.radius * sina * sinb);
    target.addToRef(this._computationVector, this._newPosition);
    this._position.copyFrom(this._newPosition);

    let up = this.upVector;
    if (this.allowUpsideDown && this.beta < 0) {
      up = up.clone();
      up = up.negate();
    }

    this._computeViewMatrix(this._position, target, up);
    this._viewMatrix.addAtIndex(12, this.targetScreenOffset.x);
    this._viewMatrix.addAtIndex(13, this.targetScreenOffset.y);

    this._collisionTriggered = false;
  };

  //----------------------------------------
  public caculateComputationVector(alpha: number, beta: number, radius: number, target: Vector3) {
    // Compute
    const cosa = Math.cos(alpha);
    const sina = Math.sin(alpha);
    const cosb = Math.cos(beta);
    let sinb = Math.sin(radius);

    if (sinb === 0) {
      sinb = 0.0001;
    }

    if (radius === 0) {
      radius = 0.0001; // Just to avoid division by zero
    }

    this._computationVector.copyFromFloats(radius * cosa * sinb, radius * cosb, radius * sina * sinb);

    if (this._upVector.x !== 0 || this._upVector.y !== 1.0 || this._upVector.z !== 0) {
      Vector3.TransformCoordinatesToRef(this._computationVector, this._yToUpMatrix, this._computationVector);
    }

    const newPosition = target.add(this._computationVector);
    return newPosition;
  }

  public override _getViewMatrix(): Matrix {
    // Compute
    const cosa = Math.cos(this.alpha);
    const sina = Math.sin(this.alpha);
    const cosb = Math.cos(this.beta);
    let sinb = Math.sin(this.beta);

    if (sinb === 0) {
      sinb = 0.0001;
    }

    const target = this._getTargetPosition();
    this._computationVector.copyFromFloats(this.radius * cosa * sinb, this.radius * cosb, this.radius * sina * sinb);

    // Rotate according to up vector
    if (this._upVector.x !== 0 || this._upVector.y !== 1.0 || this._upVector.z !== 0) {
      Vector3.TransformCoordinatesToRef(this._computationVector, this._yToUpMatrix, this._computationVector);
    }

    target.addToRef(this._computationVector, this._newPosition);
    if (this.getScene().collisionsEnabled && this.checkCollisions) {
      const coordinator = this.getScene().collisionCoordinator;
      if (!this._collider) {
        this._collider = coordinator.createCollider();
      }
      this._collider._radius = this.collisionRadius;
      this._newPosition.subtractToRef(this._position, this._collisionVelocity);
      this._collisionTriggered = true;
      coordinator.getNewPosition(
        this._position,
        this._collisionVelocity,
        this._collider,
        3,
        null,
        this._onCollisionPositionChange,
        this.uniqueId
      );
    } else {
      this._position.copyFrom(this._newPosition);

      let up = this.upVector;
      if (this.allowUpsideDown && sinb < 0) {
        up = up.negate();
      }

      this._computeViewMatrix(this._position, target, up);

      this._viewMatrix.addAtIndex(12, this.targetScreenOffset.x);
      this._viewMatrix.addAtIndex(13, this.targetScreenOffset.y);
    }

    this._currentTarget = target;
    return this._viewMatrix;
  }

  //----------------------------------------
  public override get position(): Vector3 {
    return this._position;
  }
  protected override set position(newPosition: Vector3) {
    this._setPosition(newPosition);
  }
  protected _getTargetPosition(): Vector3 {
    if (this._targetHost && this._targetHost.getAbsolutePosition) {
      const pos: Vector3 = this._targetHost.getAbsolutePosition();
      if (this._targetBoundingCenter) {
        pos.addToRef(this._targetBoundingCenter, this._target);
      } else {
        this._target.copyFrom(pos);
      }
    }

    const lockedTargetPosition = this._getLockedTargetPosition();

    if (lockedTargetPosition) {
      return lockedTargetPosition;
    }

    return this._target;
  }
  public _setPosition(position: Vector3): void {
    if (this._position.equals(position)) {
      return;
    }
    this._position.copyFrom(position);

    this.rebuildAnglesAndRadius();
  }

  //----------------------------------------
  protected _target: Vector3 = Vector3.Zero();
  protected _targetHost?: Nullable<AbstractMesh> | undefined;
  protected _targetBoundingCenter: Nullable<Vector3> = null;

  public override get target(): Vector3 {
    return this._target;
  }
  public override set target(value: Vector3) {
    this.setTarget(value);
  }
  public override getTarget(): Vector3 {
    return this.target;
  }
  public override setTarget(
    target: AbstractMesh | Vector3,
    toBoundingCenter = false,
    allowSamePosition = false,
    cloneAlphaBetaRadius = false
  ): void {
    if ((<any>target).getBoundingInfo) {
      if (toBoundingCenter) {
        this._targetBoundingCenter = (<any>target).getBoundingInfo().boundingBox.centerWorld.clone();
      } else {
        this._targetBoundingCenter = null;
      }
      (<AbstractMesh>target).computeWorldMatrix();
      this._targetHost = <AbstractMesh>target;
      this._target = this._getTargetPosition();

      this.onMeshTargetChangedObservable.notifyObservers(this._targetHost);
    } else {
      const newTarget = <Vector3>target;
      const currentTarget = this._getTargetPosition();
      if (currentTarget && !allowSamePosition && currentTarget.equals(newTarget)) {
        return;
      }
      this._targetHost = null;
      this._target = newTarget;
      this._targetBoundingCenter = null;
      this.onMeshTargetChangedObservable.notifyObservers(null);
    }

    if (!cloneAlphaBetaRadius) {
      this.rebuildAnglesAndRadius();
    }
  }

  //----------------------------------------
  protected _upToYMatrix!: Matrix;
  protected _yToUpMatrix!: Matrix;
  override set upVector(vec: Vector3) {
    if (!this._upToYMatrix) {
      this._yToUpMatrix = new Matrix();
      this._upToYMatrix = new Matrix();

      this._upVector = Vector3.Zero();
    }

    vec.normalize();
    this._upVector.copyFrom(vec);
    this.setMatUp();
  }
  override get upVector() {
    return this._upVector;
  }
  public setMatUp() {
    Matrix.RotationAlignToRef(Vector3.UpReadOnly, this._upVector, this._yToUpMatrix);
    Matrix.RotationAlignToRef(this._upVector, Vector3.UpReadOnly, this._upToYMatrix);
  }

  //----------------------------------------
  // Core
  public rebuildAnglesAndRadius(): void {
    let result = this.calculateLimit(this.calculateAnglesAndRadius(this._position));

    this.radius = result.radius;
    this.alpha = result.alpha;
    this.beta = result.beta;
    this.fov = result.fov;

    this._checkLimits();
  }
  public calculateAnglesAndRadius(position: Vector3, targetPosition?: Vector3, fov?: number) {
    position.subtractToRef(targetPosition ?? this._getTargetPosition(), this._computationVector);

    // need to rotate to Y up equivalent if up vector not Axis.Y
    if (this._upVector.x !== 0 || this._upVector.y !== 1.0 || this._upVector.z !== 0) {
      Vector3.TransformCoordinatesToRef(this._computationVector, this._upToYMatrix, this._computationVector);
    }

    let radius = this._computationVector.length();
    if (radius === 0) {
      radius = 0.0001; // Just to avoid division by zero
    }

    // Alpha
    let alpha = this.alpha;
    const previousAlpha = this.alpha;
    if (this._computationVector.x === 0 && this._computationVector.z === 0) {
      alpha = Math.PI / 2; // avoid division by zero when looking along up axis, and set to acos(0)
    } else {
      alpha = Math.acos(
        this._computationVector.x /
          Math.sqrt(Math.pow(this._computationVector.x, 2) + Math.pow(this._computationVector.z, 2))
      );
    }

    if (this._computationVector.z < 0) {
      alpha = 2 * Math.PI - alpha;
    }

    // Calculate the number of revolutions between the new and old alpha values.
    const alphaCorrectionTurns = Math.round((previousAlpha - alpha) / (2.0 * Math.PI));
    // Adjust alpha so that its numerical representation is the closest one to the old value.
    alpha += alphaCorrectionTurns * 2.0 * Math.PI;

    // Beta
    let beta = Math.acos(this._computationVector.y / radius);

    return {
      alpha: alpha,
      beta: beta,
      radius: radius,
      fov: fov ?? this.fov,
    };
  }
  public calculateLimit(result: ReturnType<typeof this.calculateAnglesAndRadius>) {
    if (this.lowerBetaLimit === null || this.lowerBetaLimit === undefined) {
      if (this.allowUpsideDown && result.beta > Math.PI) {
        result.beta = result.beta - 2 * Math.PI;
      }
    } else {
      if (result.beta < this.lowerBetaLimit) {
        result.beta = this.lowerBetaLimit;
      }
    }

    if (this.upperBetaLimit === null || this.upperBetaLimit === undefined) {
      if (this.allowUpsideDown && result.beta < -Math.PI) {
        result.beta = result.beta + 2 * Math.PI;
      }
    } else {
      if (result.beta > this.upperBetaLimit) {
        result.beta = this.upperBetaLimit;
      }
    }

    if (this.lowerAlphaLimit !== null && result.alpha < this.lowerAlphaLimit) {
      result.alpha = this.lowerAlphaLimit;
    }
    if (this.upperAlphaLimit !== null && result.alpha > this.upperAlphaLimit) {
      result.alpha = this.upperAlphaLimit;
    }

    if (this.lowerRadiusLimit !== null && result.radius < this.lowerRadiusLimit) {
      result.radius = this.lowerRadiusLimit;
      this.inertialRadiusOffset = 0;
    }
    if (this.upperRadiusLimit !== null && result.radius > this.upperRadiusLimit) {
      result.radius = this.upperRadiusLimit;
      this.inertialRadiusOffset = 0;
    }

    if (this.lowerFovLimit !== null && result.fov < this.lowerFovLimit) {
      result.fov = this.lowerFovLimit;
      this.inertialFov = 0;
    }
    if (this.upperFovLimit !== null && result.fov > this.upperFovLimit) {
      result.fov = this.upperFovLimit;
      this.inertialFov = 0;
    }
    return result;
  }

  //----------------------------------------
  // Update
  public override _checkInputs(): void {
    if (this._collisionTriggered) return;

    this.inputManager._checkInputs();

    let nextAlpha: number | undefined = undefined;
    let nextBeta: number | undefined = undefined;
    let nextRadius: number | undefined = undefined;
    let nextTargetOffset: Vector3 | undefined = undefined;
    let nextTarget: Vector3 | undefined = undefined;

    // Inertia
    if (this.inertialAlphaOffset !== 0 || this.inertialBetaOffset !== 0 || this.inertialRadiusOffset !== 0) {
      const directionModifier = this.invertRotation ? -1 : 1;
      let inertialAlphaOffset = this.inertialAlphaOffset;
      if (this.beta <= 0) {
        inertialAlphaOffset *= -1;
      }
      if (this.getScene().useRightHandedSystem) {
        inertialAlphaOffset *= -1;
      }
      if (this.parent && this.parent._getWorldMatrixDeterminant() < 0) {
        inertialAlphaOffset *= -1;
      }

      nextAlpha = this.alpha + inertialAlphaOffset * directionModifier;
      nextBeta = this.beta + this.inertialBetaOffset * directionModifier;
      nextRadius = this.radius - this.inertialRadiusOffset;

      this.alpha = nextAlpha;

      this.inertialAlphaOffset *= this.inertia;
      this.inertialBetaOffset *= this.inertia;
      this.inertialRadiusOffset *= this.inertia;
      if (Math.abs(this.inertialAlphaOffset) < Epsilon) {
        this.inertialAlphaOffset = 0;
      }
      if (Math.abs(this.inertialBetaOffset) < Epsilon) {
        this.inertialBetaOffset = 0;
      }
      if (Math.abs(this.inertialRadiusOffset) < this.speed * Epsilon) {
        this.inertialRadiusOffset = 0;
      }
    }

    if (this.inertialTargetOffset !== Vector3.ZeroReadOnly) {
      nextTargetOffset = this.inertialTargetOffset;
      this.inertialTargetOffset = this.inertialTargetOffset.scale(this.inertia);

      const ep = this.speed * Epsilon;
      if (Math.abs(this.inertialTargetOffset.x) < ep) {
        this.inertialTargetOffset.x = 0;
      }
      if (Math.abs(this.inertialTargetOffset.y) < ep) {
        this.inertialTargetOffset.y = 0;
      }
      if (Math.abs(this.inertialTargetOffset.z) < ep) {
        this.inertialTargetOffset.z = 0;
      }
    }

    let cameraTransformMatrix: Matrix | undefined = undefined;
    let transformedDirection: Vector3 | undefined = undefined;

    // Panning inertia
    if (this.inertialPanningX !== 0 || this.inertialPanningY !== 0) {
      const viewMatrix = this._viewMatrix.clone();
      cameraTransformMatrix = this._cameraTransformMatrix.clone();
      transformedDirection = this._transformedDirection.clone();

      const localDirection = new Vector3(this.inertialPanningX, this.inertialPanningY, this.inertialPanningY);

      const invertMatrix = viewMatrix.invert();
      localDirection.multiplyInPlace(this.panningAxis);
      transformedDirection = Vector3.TransformNormal(localDirection, cameraTransformMatrix);

      cameraTransformMatrix = invertMatrix;

      // Eliminate y if mapPanning is enabled
      if (!this.panningAxis.y) {
        transformedDirection.y = 0;
      }

      transformedDirection.x = this.xAxisPanningLock ? 0 : transformedDirection.x;
      transformedDirection.y = this.yAxisPanningLock ? 0 : transformedDirection.y;
      transformedDirection.z = this.zAxisPanningLock ? 0 : transformedDirection.z;

      if (!this._targetHost) {
        const panningOffset = this.computePanningOffset(transformedDirection);
        nextTarget = this._target.add(panningOffset);
      }

      this.inertialPanningX *= this.panningInertia;
      this.inertialPanningY *= this.panningInertia;

      if (Math.abs(this.inertialPanningX) < this.speed * Epsilon) {
        this.inertialPanningX = 0;
      }
      if (Math.abs(this.inertialPanningY) < this.speed * Epsilon) {
        this.inertialPanningY = 0;
      }
    }

    if (this.inertialFov !== 0) {
      this.fov += this.inertialFov;
      this.inertialFov *= this.inertia;
      if (Math.abs(this.inertialAlphaOffset) < this.speed * Epsilon) {
        this.inertialFov = 0;
      }
    }

    // z-fighting 방지
    const distance = this.radius;
    this.minZ = distance * 0.005; // 10m 기준 0.05 min 값 가지도록
    if (distance > 20) this.minZ += 0.5; // 충분히 멀 경우는 넉넉하게
    this.minZ = clamp(this.minZ, 0.05, 1);

    // next 좌표가 지정한 lowerGroundToDistance 밑으로 내려가지 않도록 계산 후 적용
    const a = nextAlpha ?? this.alpha;
    const b = nextBeta ?? this.beta;
    const r = nextRadius ?? this.radius;
    const t = (nextTarget ?? this.target).add(nextTargetOffset ?? Vector3.Zero());
    if (
      this.lowerGroundToDistance == null ||
      this.caculateComputationVector(a, b, r, t).y > this.lowerGroundToDistance
    ) {
      if (nextBeta) this.beta = b;
      if (nextRadius) this.radius = r;
      if (cameraTransformMatrix) this._cameraTransformMatrix = cameraTransformMatrix;
      if (transformedDirection) this._transformedDirection = transformedDirection;
      if (nextTarget || nextTargetOffset) this._target = t;
    }

    // Limits
    this._checkLimits();
    super._checkInputs();
  }
  protected _checkLimits() {
    let result = this.calculateLimit({
      alpha: this.alpha,
      beta: this.beta,
      radius: this.radius,
      fov: this.fov,
    });
    this.radius = result.radius;
    this.alpha = result.alpha;
    this.beta = result.beta;
  }
  //----------------------------------------
  protected calculatePosition(position: Vector3, isRelative: boolean = false, isLeftHandCoordinate: boolean = true) {
    if (isLeftHandCoordinate) {
      position = convertRightToLeftHanded(position, true);
    }
    const target = isRelative ? this.target.clone().add(position) : position;
    const radius = 0;
    return {
      target: target,
      radius: radius,
    };
  }
  protected calculateRotation(
    rotation: Vector3,
    isRelative: boolean = false,
    isRadian: boolean = false,
    isLeftHandCoordinate: boolean = true
  ) {
    if (isLeftHandCoordinate) {
      rotation = convertRightToLeftHandedRotation(rotation);
    }
    if (!isRelative) {
      // z축 방향을 바라보는 것을 기본으로 설정
      const adjustmentValue = isRadian ? Math.PI / 2 : 90;
      rotation = rotation.addInPlaceFromFloats(adjustmentValue, adjustmentValue, 0);
    }
    const radian = isRadian ? 1 : toRadian;
    const alpha = isRelative ? this.alpha + rotation.y * radian : rotation.y * radian;
    const beta = isRelative ? this.beta + rotation.x * radian : rotation.x * radian;
    return {
      alpha: alpha,
      beta: beta,
    };
  }

  public calculateDistanceToGround() {
    const cameraDirection = this.getDirection(Vector3.LeftHandedForwardReadOnly);
    const limit = -15 * toRadian;
    if (cameraDirection.y > limit) {
      cameraDirection.y = limit;
    }

    const distance = -this.position.y / cameraDirection.y;
    return distance >= 0 ? distance : 0;
  }

  public setPosition(
    position: Vector3,
    isRelative: boolean = false,
    isLeftHandCoordinate: boolean = true,
    render: boolean = true
  ) {
    const result = this.calculatePosition(position, isRelative, isLeftHandCoordinate);

    this.setTarget(result.target, false, false, true);
    this.radius = result.radius;

    if (render) this.renderManager.renderUpdate();
  }
  public setRotation(
    rotation: Vector3,
    isRelative: boolean = false,
    isRadian: boolean = false,
    isLeftHandCoordinate: boolean = true,
    render: boolean = true
  ) {
    const result = this.calculateRotation(rotation, isRelative, isRadian, isLeftHandCoordinate);

    this.alpha = result.alpha;
    this.beta = result.beta;
    this.radius = 0;

    if (render) this.renderManager.renderUpdate();
  }
  //----------------------------------------
  public async moveToCoordinate(
    position: Vector3,
    rotation: Vector3 = Vector3.Zero(),
    animationOption?: Partial<CameraAnimationOption>,
    isRelative: boolean = false,
    isRadian: boolean = false,
    isLeftHandCoordinate: boolean = true
  ): Promise<void> {
    const pos = this.calculatePosition(position, isRelative, isLeftHandCoordinate);
    const rot = this.calculateRotation(rotation, isRelative, isRadian, isLeftHandCoordinate);
    const toTarget = pos.target;
    const toAlpha = rot.alpha;
    const toBeta = rot.beta;
    const toRadius = pos.radius;

    await this.moveToAnimation(toTarget, toAlpha, toBeta, toRadius, undefined, animationOption);
  }
  public async moveToTarget(
    transform: TransformNode,
    distance: number,
    fitDistance: boolean = false,
    fitMargin: number = 1,
    animationOption?: Partial<CameraAnimationOption>
  ): Promise<void> {
    let position = transform.absolutePosition;

    const dir = transform.forward.normalize();
    const result = this.calculateAnglesAndRadius(position, position.add(dir.scale(distance)));
    const toTarget = position;
    const toAlpha = result.alpha;
    const toBeta = result.beta;
    let toRadius = result.radius;
    if (fitDistance) {
      const bounds = transform.getHierarchyBoundingVectors();
      toRadius = getFitRadius(this, bounds, fitMargin, false).radius;
    }
    await this.moveToAnimation(toTarget, toAlpha, toBeta, toRadius, undefined, animationOption);
  }
  public async moveTo(
    target?: Vector3,
    alpha?: number,
    beta?: number,
    radius?: number,
    animationOption?: Partial<CameraAnimationOption>
  ) {
    if (target) {
      const result = this.calculateAnglesAndRadius(target, target.add(Vector3.Forward().scale(radius ?? this.radius)));
      alpha = alpha ?? result.alpha;
      beta = beta ?? result.beta;
      radius = radius ?? result.radius;
    }

    await this.moveToAnimation(target, alpha, beta, radius, undefined, animationOption);
  }

  public async moveToTargetByScreenCoordinate(x: number, y: number, r: number): Promise<void> {
    const scene = this.getScene();
    const pickInfo = scene.pick(x, y);

    if (pickInfo && pickInfo.pickedPoint) {
      this.moveTo(pickInfo.pickedPoint, this.alpha, this.beta, r);
    }
  }

  public async transition(
    position: Vector3,
    targetPosition: Vector3,
    fov: number,
    animationOption?: Partial<CameraAnimationOption>
  ): Promise<void> {
    const result = this.calculateLimit(this.calculateAnglesAndRadius(position, targetPosition, fov));
    await this.moveToAnimation(targetPosition, result.alpha, result.beta, result.radius, result.fov, animationOption);
  }

  //----------------------------------------
  public getDefaultExtends?: (() => { min: Vector3; max: Vector3 } | undefined) | undefined;
  public bindGetDefaultExtends(getter?: typeof this.getDefaultExtends) {
    if (getter !== undefined) this.getDefaultExtends = getter;
  }
  private getExtendsOrSceneExtends(): { min: Vector3; max: Vector3 } {
    return this.getDefaultExtends
      ? this.getDefaultExtends() ?? this.getScene().getWorldExtends()
      : this.getScene().getWorldExtends();
  }

  //----------------------------------------
  public async fitToTopView(
    animationOption?: Partial<CameraAnimationOption>,
    fitMargin: number = 1.2,
    rotateable: boolean = false,
    pivotPosition: Vector3 = Vector3.Zero(),
    bounds?: { min: Vector3; max: Vector3 }
  ) {
    // console.log('fitToTopView');
    const canvas = this._scene.getEngine().getRenderingCanvas();
    if (!canvas) return;

    pivotPosition = new Vector3(-pivotPosition.x, pivotPosition.y, pivotPosition.z);

    if (bounds === undefined) bounds = this.getExtendsOrSceneExtends();
    const fitResult = getFitRadius(this, bounds, fitMargin, rotateable);

    const dir = Vector3.Up();
    const result = this.calculateAnglesAndRadius(pivotPosition.add(dir.scale(fitResult.radius)), pivotPosition);
    const toTarget = pivotPosition;
    const toAlpha = fitResult.rotate ? result.alpha + -90 * toRadian : result.alpha - 180 * toRadian;
    const toBeta = result.beta;

    await this.moveToAnimation(toTarget, toAlpha, toBeta, fitResult.radius, undefined, animationOption);
  }
  public async fitToQuaterView(
    animationOption?: Partial<CameraAnimationOption>,
    fitMargin: number = 1.2,
    pivotPosition: Vector3 = Vector3.Zero(),
    bounds?: { min: Vector3; max: Vector3 }
  ) {
    pivotPosition = new Vector3(-pivotPosition.x, pivotPosition.y, -pivotPosition.z);
    if (bounds === undefined) bounds = this.getExtendsOrSceneExtends();

    const fitResult = getFitRadius(this, bounds, fitMargin, false);

    const toTarget = pivotPosition;
    const toAlpha = 45 * toRadian - 180 * toRadian;
    const toBeta = 45 * toRadian;

    await this.moveToAnimation(toTarget, toAlpha, toBeta, fitResult.radius, undefined, animationOption);
  }

  //----------------------------------------
  private async moveToAnimation(
    toTarget: Vector3 | undefined,
    toAlpha: number | undefined,
    toBeta: number | undefined,
    toRadius: number | undefined,
    toFov: number | undefined,
    animOption: Partial<CameraAnimationOption> | undefined
  ) {
    if (!toTarget && !toAlpha && !toBeta && !toRadius) return;

    const animationOption = {
      ...defaultCameraAnimationOption,
      ...animOption,
    };

    this.inertia = 0;
    this.panningInertia = 0;
    this._reset();

    if (animationOption.seconds <= 0) {
      if (toTarget !== undefined) this.target = toTarget;
      if (toAlpha !== undefined) this.alpha = toAlpha;
      if (toBeta !== undefined) this.beta = toBeta;
      if (toRadius !== undefined) this.radius = toRadius;
      this.renderManager.renderUpdate();
    } else {
      const property: AnimationProperty<this>[] = [];
      if (toTarget !== undefined) {
        property.push({
          targetProperty: 'target',
          keyFrame: {
            duration: animationOption.seconds,
            from: this.target,
            to: toTarget,
          },
        });
      }

      if (toAlpha !== undefined) {
        property.push({
          targetProperty: 'alpha',
          keyFrame: {
            duration: animationOption.seconds,
            from: this.alpha,
            to: toAlpha,
          },
        });
      }

      if (toBeta !== undefined) {
        property.push({
          targetProperty: 'beta',
          keyFrame: {
            duration: animationOption.seconds,
            from: this.beta,
            to: toBeta,
          },
        });
      }

      if (toRadius !== undefined) {
        property.push({
          targetProperty: 'radius',
          keyFrame: {
            duration: animationOption.seconds,
            from: this.radius,
            to: toRadius,
          },
        });
      }

      if (toFov !== undefined) {
        property.push({
          targetProperty: 'fov',
          keyFrame: {
            duration: animationOption.seconds,
            from: this.fov,
            to: toFov,
          },
        });
      }

      if (property.length > 0) {
        await playAnimation({
          target: this,
          property: property,
          easingFunction: createEasingFunction(animationOption.easingFunction, animationOption.easingMode),
          onAnimationEnd: () => {},
        });
      }
    }
  }

  //----------------------------------------
  public async setViewport(
    viewportParam: Partial<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>,
    animationOption: Partial<CameraAnimationOption> | undefined
  ) {
    const viewport = {
      ...{
        x: this.viewport.x,
        y: this.viewport.y,
        width: this.viewport.width,
        height: this.viewport.height,
      },
      ...viewportParam,
    };

    if (animationOption) {
      const option = { ...defaultCameraAnimationOption, ...animationOption };
      if (option.seconds === 0) {
        Object.assign(this.viewport, new Viewport(viewport.x, viewport.y, viewport.width, viewport.height));
        this.renderManager.renderUpdate();
      } else {
        await playAnimation({
          target: this.viewport,
          property: [
            {
              targetProperty: 'x',
              keyFrame: {
                duration: option.seconds,
                from: this.viewport.x,
                to: viewport.x,
              },
            },
            {
              targetProperty: 'y',
              keyFrame: {
                duration: option.seconds,
                from: this.viewport.y,
                to: viewport.y,
              },
            },
            {
              targetProperty: 'width',
              keyFrame: {
                duration: option.seconds,
                from: this.viewport.width,
                to: viewport.width,
              },
            },
            {
              targetProperty: 'height',
              keyFrame: {
                duration: option.seconds,
                from: this.viewport.height,
                to: viewport.height,
              },
            },
          ],
          easingFunction: createEasingFunction(option.easingFunction, option.easingMode),
          onAnimationEnd: () => {},
        });
      }
    } else {
      Object.assign(this.viewport, new Viewport(viewport.x, viewport.y, viewport.width, viewport.height));
      this.renderManager.renderUpdate();
    }
  }

  public stopPlayingAnimation() {
    stopPlayingAnimation(this.getScene(), this);
  }

  public computePanningOffset(panning: Vector3) {
    if (!this.panningDistanceLimit) return panning;

    const result = panning.clone();
    const nextTarget = panning.add(this._target);
    if (nextTarget.y >= this.panningDistanceLimit) result.y = 0;

    const v1 = new Vector2(nextTarget.x, nextTarget.z);
    const v2 = new Vector2(this.panningOriginTarget.x, this.panningOriginTarget.y);

    const distanceSquared = Vector2.DistanceSquared(v1, v2);
    if (distanceSquared >= this.panningDistanceLimit * this.panningDistanceLimit) {
      result.x = 0;
      result.z = 0;
    }

    return result;
  }

  public getRadiusScale() {
    return (this.upperRadiusLimit ?? 1000) - (this.lowerRadiusLimit ?? 0);
  }

  public getBetaScale() {
    return (this.upperBetaLimit ?? Tools.ToRadians(90)) - (this.lowerBetaLimit ?? 0);
  }

  /**
   * zoom in/out 시 카메라가 바라보는 중심점을 보정하는 함수.
   * 최초에 카메라의 중심점은 지표면에 있다가, 카메라와의 거리가 가까워질수록 고정된 높이(지표면으로부터 10미터 위)로 올라간다.
   * (targetHeightPivot은 whenViewMatrixChanged 시점에 계산됨)
   * @param delta
   * @returns
   */
  public computeZoomAdjustOffset(delta: number): Vector3 {
    const targetHeight = this.targetHeightPivot ?? 10;
    const pivotGap = Math.abs(targetHeight - this.target.y);
    const hScale = pivotGap > 50 ? this.getRadiusScale() * 3 : 0;
    const isGoUpside = this.target.y < targetHeight;

    const heightDiff = isGoUpside ? Math.abs(delta) * hScale : -Math.abs(delta) * hScale;
    const nextHeight = this.target.y + heightDiff;

    let offset = Vector3.Zero();
    if (isGoUpside) {
      offset.y = nextHeight < targetHeight ? heightDiff : 0;
    } else {
      offset.y = nextHeight >= targetHeight ? heightDiff : 0;
    }

    if (offset.y > 0) return offset;
    if (delta <= 0) return offset;

    const upperBetaLimit = (this.upperBetaLimit ?? Tools.ToRadians(90)) - Tools.ToRadians(10);
    const lowerRadiusLimit = this.lowerRadiusLimit ?? 10;
    if (this.radius > lowerRadiusLimit) return offset;
    if (this.beta < upperBetaLimit) return offset;

    const forward = this.getDirection(Vector3.LeftHandedForwardReadOnly);
    const proj = new Vector3(forward.x, 0, forward.z).normalize();
    const forwardOffset = this.computePanningOffset(proj).scale(delta * 700);
    offset = offset.add(forwardOffset);

    return offset;
  }

  //----------------------------------------
  // Cache
  private _storedAlpha: number = 0;
  private _storedBeta: number = 0;
  private _storedRadius: number = 0;
  private _storedTarget: Vector3 = Vector3.Zero();
  private _storedTargetScreenOffset: Vector2 = Vector2.Zero();
  private storedFov: number = 0;

  private _storedUpperRadiusLimit: Nullable<number> = null;
  private _storedLowerRadiusLimit: Nullable<number> = null;
  private _storedUpperBetaLimit: Nullable<number> = null;
  private _storedLowerBetaLimit: Nullable<number> = null;

  public override _initCache(): void {
    super._initCache();
    this._cache._target = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    this._cache.alpha = undefined;
    this._cache.beta = undefined;
    this._cache.radius = undefined;
    this._cache.targetScreenOffset = Vector2.Zero();
  }
  public override _updateCache(ignoreParentClass?: boolean): void {
    if (!ignoreParentClass) {
      super._updateCache();
    }

    this._cache._target.copyFrom(this._getTargetPosition());
    this._cache.alpha = this.alpha;
    this._cache.beta = this.beta;
    this._cache.radius = this.radius;
    this._cache.targetScreenOffset.copyFrom(this.targetScreenOffset);
  }
  public override storeState(): Camera {
    this._storedAlpha = this.alpha;
    this._storedBeta = this.beta;
    this._storedRadius = this.radius;
    this._storedTarget = this._getTargetPosition().clone();
    this._storedTargetScreenOffset = this.targetScreenOffset.clone();
    this.storedFov = this.fov;

    this._storedUpperRadiusLimit = this.upperRadiusLimit;
    this._storedLowerRadiusLimit = this.lowerRadiusLimit;
    this._storedUpperBetaLimit = this.upperBetaLimit;
    this._storedLowerBetaLimit = this.lowerBetaLimit;
    return super.storeState();
  }
  public restoreStateValuesExceptTransform() {
    this.inertialAlphaOffset = 0;
    this.inertialBetaOffset = 0;
    this.inertialRadiusOffset = 0;
    this.inertialTargetOffset = Vector3.Zero();
    this.inertialPanningX = 0;
    this.inertialPanningY = 0;
    this.inertialFov = 0;

    this.fov = this.storedFov;
    this.upperRadiusLimit = this._storedUpperRadiusLimit;
    this.lowerRadiusLimit = this._storedLowerRadiusLimit;
    this.upperBetaLimit = this._storedUpperBetaLimit;
    this.lowerBetaLimit = this._storedLowerBetaLimit;
  }
  public override _restoreStateValues(): boolean {
    if (!super._restoreStateValues()) {
      return false;
    }

    this.setTarget(this._storedTarget.clone());
    this.alpha = this._storedAlpha;
    this.beta = this._storedBeta;
    this.radius = this._storedRadius;
    this.targetScreenOffset = this._storedTargetScreenOffset.clone();

    this.inertialAlphaOffset = 0;
    this.inertialBetaOffset = 0;
    this.inertialRadiusOffset = 0;
    this.inertialTargetOffset = Vector3.Zero();
    this.inertialPanningX = 0;
    this.inertialPanningY = 0;
    this.inertialFov = 0;

    return true;
  }
  public override _isSynchronizedViewMatrix(): boolean {
    if (!super._isSynchronizedViewMatrix()) {
      return false;
    }

    return (
      this._cache._target.equals(this._getTargetPosition()) &&
      this._cache.alpha === this.alpha &&
      this._cache.beta === this.beta &&
      this._cache.radius === this.radius &&
      this._cache.targetScreenOffset.equals(this.targetScreenOffset)
    );
  }
}
